/**
 * API client for backend calls that require Firebase Admin SDK operations.
 * Uses Firebase Auth ID tokens for authentication.
 */
import { auth, db } from './firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';

const API_BASE = import.meta.env.VITE_API_URL || 'https://fs2.equosnine.com';
const UAC_ADMIN_BASE = import.meta.env.VITE_UAC_ADMIN_URL || 'https://info1.equosnine.com';

/**
 * Get authorization headers with current user's ID token.
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Disable a user account. This:
 * 1. Disables the user in Firebase Auth (prevents login)
 * 2. Updates the Firestore user document with isActive: false
 *
 * @param uid - The user ID to disable
 */
export async function disableUser(uid: string): Promise<void> {
  const response = await fetch(`${API_BASE}/api/users/${uid}/disable`, {
    method: 'POST',
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to disable user' }));
    throw new Error(error.error || error.message || 'Failed to disable user');
  }
}

/**
 * Enable a user account. This:
 * 1. Re-enables the user in Firebase Auth (allows login)
 * 2. Updates the Firestore user document with isActive: true
 *
 * @param uid - The user ID to enable
 */
export async function enableUser(uid: string): Promise<void> {
  const response = await fetch(`${API_BASE}/api/users/${uid}/enable`, {
    method: 'POST',
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to enable user' }));
    throw new Error(error.error || error.message || 'Failed to enable user');
  }
}

/**
 * Invitation email data structure
 */
export interface InvitationEmailData {
  email: string;
  name: string;
  role: string;
  inviterName: string;
  inviterEmail: string;
  businessName: string;
  inviteLink: string;
  expiresAt: string;
  // Claim data for Firebase Auth user creation
  claimData?: {
    businessId: string | null;
    userType: string;
    allowedModules: string[];
    allowedLocationIds?: string[] | null;
    allowedBusinessIds?: string[] | null;
    locationId?: string | null;
    teamOwnerId?: string | null;
  };
}

/**
 * Send a team member invitation email via UAC Admin server.
 * Uses the team_invitation email template.
 *
 * @param invitation - The invitation details
 * @returns Promise that resolves when email is sent
 */
export async function sendInvitationEmail(invitation: InvitationEmailData): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${UAC_ADMIN_BASE}/api/email/template/team_invitation`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({
        recipient: invitation.email,
        data: {
          inviteeName: invitation.name,
          inviterName: invitation.inviterName,
          inviterEmail: invitation.inviterEmail,
          businessName: invitation.businessName,
          role: invitation.role,
          inviteLink: invitation.inviteLink,
          expiresAt: invitation.expiresAt,
        },
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to send invitation email' }));
      console.error('Invitation email failed:', error);
      return { success: false, error: error.error || error.message || 'Failed to send invitation email' };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Invitation email error:', error);
    return { success: false, error: error.message || 'Failed to send invitation email' };
  }
}

/**
 * Send a direct invitation email (fallback if template not available).
 * Uses the generic email send endpoint.
 *
 * @param invitation - The invitation details
 * @returns Promise that resolves when email is sent
 */
export async function sendDirectInvitationEmail(invitation: InvitationEmailData): Promise<{ success: boolean; error?: string }> {
  try {
    const htmlBody = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #667eea;">You've been invited to join ${invitation.businessName}</h2>
        <p>Hi ${invitation.name},</p>
        <p><strong>${invitation.inviterName}</strong> (${invitation.inviterEmail}) has invited you to join their team as a <strong>${invitation.role}</strong>.</p>
        <p style="margin: 24px 0;">
          <a href="${invitation.inviteLink}"
             style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    padding: 12px 24px;
                    text-decoration: none;
                    border-radius: 6px;
                    display: inline-block;">
            Accept Invitation
          </a>
        </p>
        <p style="color: #666; font-size: 14px;">
          This invitation expires on ${invitation.expiresAt}. If you have questions, contact ${invitation.inviterEmail}.
        </p>
        <hr style="border: none; border-top: 1px solid #eee; margin: 24px 0;" />
        <p style="color: #999; font-size: 12px;">
          If you didn't expect this invitation, you can safely ignore this email.
        </p>
      </div>
    `;

    const response = await fetch(`${UAC_ADMIN_BASE}/api/email/send`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({
        to: invitation.email,
        subject: `${invitation.inviterName} invited you to join ${invitation.businessName}`,
        body: `Hi ${invitation.name}, you've been invited to join ${invitation.businessName} as a ${invitation.role}. Accept your invitation here: ${invitation.inviteLink}. This invitation expires on ${invitation.expiresAt}.`,
        htmlBody: htmlBody,
        isHtml: true,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to send invitation email' }));
      console.error('Direct invitation email failed:', error);
      return { success: false, error: error.error || error.message || 'Failed to send invitation email' };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Direct invitation email error:', error);
    return { success: false, error: error.message || 'Failed to send invitation email' };
  }
}

/**
 * Communication queue item type
 */
export type CommunicationType = 'team_invitation' | 'business_invitation' | 'welcome' | 'password_reset' | 'notification';

/**
 * Welcome email data structure.
 * The backend automatically generates the password_set_link when processing welcome template types.
 */
export interface WelcomeEmailData {
  email: string;
  firstName?: string;
  companyName?: string;
  supportEmail?: string;
  year?: string;
  companyAddress?: string;
  /** Where this welcome email originated from */
  source: 'signup' | 'invitation' | 'business_invitation' | 'admin_approval';
}

/**
 * Business invitation email data structure
 */
export interface BusinessInvitationEmailData {
  email: string;
  inviteeName: string;
  inviteeCompanyName: string;
  inviterName: string;
  inviterEmail: string;
  inviterBusinessName: string;
  warehouseNames: string[];
  inviteLink: string;
  expiresAt: string;
}

/**
 * Queue an email to be sent via the communications_queue collection.
 * A Cloud Function will process the queue and send the emails.
 *
 * @param type - The type of communication
 * @param invitation - The invitation email data
 * @returns Promise that resolves when the email is queued
 */
export async function queueInvitationEmail(invitation: InvitationEmailData): Promise<{ success: boolean; error?: string }> {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.error('queueInvitationEmail: User not authenticated');
      return { success: false, error: 'User not authenticated' };
    }

    console.log('queueInvitationEmail: Queueing email for', invitation.email);

    const queueData = {
      // Use template ID 12 (Team Invitation) for Listmonk
      templateId: 12,
      templateType: 'new_invite',
      recipients: [invitation.email],
      variables: {
        invitee_name: invitation.name,
        inviter_name: invitation.inviterName,
        inviter_email: invitation.inviterEmail,
        business_name: invitation.businessName,
        role: invitation.role,
        invite_link: invitation.inviteLink,
        expires_at: invitation.expiresAt,
        // Note: password_set_link will be auto-generated by backend after user creation
      },
      // Include claim data for Firebase Auth user creation
      // The Go backend will create the user and set these claims
      claimData: invitation.claimData ? {
        businessId: invitation.claimData.businessId,
        userType: invitation.claimData.userType,
        allowedModules: invitation.claimData.allowedModules,
        allowedLocationIds: invitation.claimData.allowedLocationIds || null,
        allowedBusinessIds: invitation.claimData.allowedBusinessIds || null,
        locationId: invitation.claimData.locationId || null,
        teamOwnerId: invitation.claimData.teamOwnerId || null,
      } : null,
      status: 'pending',
      attempts: 0,
      lastError: '',
      lock: false,
      lockedBy: '',
      createdAt: new Date().toISOString(),
      metadata: {
        queuedAt: new Date().toISOString(),
        source: 'portal_staff_management',
        createdBy: user.uid,
        createUser: true, // Flag to tell backend to create Firebase Auth user
      },
    };

    console.log('queueInvitationEmail: Writing to communications_queue', queueData);

    const queueRef = collection(db, 'communications_queue');
    const docRef = await addDoc(queueRef, queueData);

    console.log('queueInvitationEmail: Successfully queued with ID:', docRef.id);

    return { success: true };
  } catch (error: any) {
    console.error('queueInvitationEmail: Failed to queue email:', error);
    return { success: false, error: error.message || 'Failed to queue email' };
  }
}

/**
 * Queue a business invitation email to be sent via the communications_queue collection.
 * A Cloud Function will process the queue and send the emails.
 *
 * @param invitation - The business invitation email data
 * @returns Promise that resolves when the email is queued
 */
export async function queueBusinessInvitationEmail(invitation: BusinessInvitationEmailData): Promise<{ success: boolean; error?: string }> {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.error('queueBusinessInvitationEmail: User not authenticated');
      return { success: false, error: 'User not authenticated' };
    }

    console.log('queueBusinessInvitationEmail: Queueing email for', invitation.email);

    const queueData = {
      templateType: 'business_invitation',
      recipients: [invitation.email],
      variables: {
        invitee_name: invitation.inviteeName,
        invitee_company_name: invitation.inviteeCompanyName,
        inviter_name: invitation.inviterName,
        inviter_email: invitation.inviterEmail,
        inviter_business_name: invitation.inviterBusinessName,
        warehouse_names: invitation.warehouseNames.join(', '),
        invite_link: invitation.inviteLink,
        expires_at: invitation.expiresAt,
      },
      status: 'pending',
      attempts: 0,
      lastError: '',
      lock: false,
      lockedBy: '',
      createdAt: new Date().toISOString(),
      metadata: {
        queuedAt: new Date().toISOString(),
        source: 'portal_business_invitations',
        createdBy: user.uid,
        invitationType: 'business',
      },
    };

    console.log('queueBusinessInvitationEmail: Writing to communications_queue', queueData);

    const queueRef = collection(db, 'communications_queue');
    const docRef = await addDoc(queueRef, queueData);

    console.log('queueBusinessInvitationEmail: Successfully queued with ID:', docRef.id);

    return { success: true };
  } catch (error: any) {
    console.error('queueBusinessInvitationEmail: Failed to queue email:', error);
    return { success: false, error: error.message || 'Failed to queue email' };
  }
}

/**
 * Queue a welcome email to be sent via the communications_queue collection.
 * The backend processor automatically generates a password reset link for welcome emails.
 * Uses Listmonk template ID 5 (Welcome-New).
 *
 * @param welcome - The welcome email data
 * @returns Promise that resolves when the email is queued
 */
export async function queueWelcomeEmail(welcome: WelcomeEmailData): Promise<{ success: boolean; error?: string }> {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.error('queueWelcomeEmail: User not authenticated');
      return { success: false, error: 'User not authenticated' };
    }

    console.log('queueWelcomeEmail: Queueing welcome email for', welcome.email);

    const queueData = {
      // Use template ID 5 (Welcome-New) for Listmonk
      // Also include templateType for password link generation detection
      templateId: 5,
      templateType: 'welcome',
      recipients: [welcome.email],
      variables: {
        first_name: welcome.firstName || '',
        company_name: welcome.companyName || 'EQUOS',
        support_email: welcome.supportEmail || 'support@equosnine.com',
        year: welcome.year || new Date().getFullYear().toString(),
        company_address: welcome.companyAddress || '',
        // Note: password_set_link is auto-generated by the backend processor
      },
      status: 'pending',
      attempts: 0,
      lastError: '',
      lock: false,
      lockedBy: '',
      createdAt: new Date().toISOString(),
      metadata: {
        queuedAt: new Date().toISOString(),
        source: `portal_${welcome.source}`,
        createdBy: user.uid,
        welcomeSource: welcome.source,
      },
    };

    console.log('queueWelcomeEmail: Writing to communications_queue', queueData);

    const queueRef = collection(db, 'communications_queue');
    const docRef = await addDoc(queueRef, queueData);

    console.log('queueWelcomeEmail: Successfully queued with ID:', docRef.id);

    return { success: true };
  } catch (error: any) {
    console.error('queueWelcomeEmail: Failed to queue email:', error);
    return { success: false, error: error.message || 'Failed to queue email' };
  }
}

// ============================================================================
// PASSWORD RESET - Queue-based (writes to communications_queue)
// ============================================================================

/**
 * Request a password reset email.
 * Writes to communications_queue for backend processing.
 * Returns success even for non-existent emails to prevent enumeration.
 *
 * Note: Security rules must allow unauthenticated writes to communications_queue
 * for documents with templateType: "password_reset_request"
 */
export async function requestPasswordReset(email: string): Promise<{ success: boolean; error?: string }> {
  try {
    const normalizedEmail = email.toLowerCase().trim();

    // Basic email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(normalizedEmail)) {
      return { success: false, error: 'Please enter a valid email address' };
    }

    // Extract first name from email for template
    const firstName = normalizedEmail.split('@')[0];

    const queueData = {
      templateId: 11, // Listmonk template ID for password reset
      templateType: 'password_reset',
      recipients: [normalizedEmail],
      variables: {
        first_name: firstName,
        email: normalizedEmail,
        expiry_time: '1 hour',
      },
      status: 'pending',
      attempts: 0,
      lastError: '',
      lock: false,
      lockedBy: '',
      createdAt: new Date().toISOString(),
      metadata: {
        source: 'portal_password_reset',
        queuedAt: new Date().toISOString(),
      },
    };

    console.log('requestPasswordReset: Queueing password reset for', normalizedEmail);

    const queueRef = collection(db, 'public_comms');
    await addDoc(queueRef, queueData);

    console.log('requestPasswordReset: Successfully queued to public_comms');

    // Always return success to prevent email enumeration
    return { success: true };
  } catch (error: any) {
    console.error('requestPasswordReset error:', error);
    // Still return success to prevent enumeration - backend will handle invalid emails
    return { success: true };
  }
}

/**
 * Validate a password reset token.
 * Returns the email associated with the token if valid.
 *
 * @param token - The password reset token from the URL (oobCode)
 * @returns Promise with validation result
 */
export async function validateResetToken(token: string): Promise<{ valid: boolean; email?: string; error?: string }> {
  try {
    // Firebase verifyPasswordResetCode returns the email if the code is valid
    const email = await verifyPasswordResetCode(auth, token);

    // Mask the email for display (show first 2 chars + domain)
    const [localPart, domain] = email.split('@');
    const maskedEmail = localPart.length > 2
      ? `${localPart.slice(0, 2)}${'*'.repeat(Math.min(localPart.length - 2, 5))}@${domain}`
      : `${localPart}@${domain}`;

    return { valid: true, email: maskedEmail };
  } catch (error: any) {
    console.error('validateResetToken error:', error);

    // Map Firebase error codes to user-friendly messages
    let errorMessage = 'Invalid or expired reset link.';
    if (error.code === 'auth/expired-action-code') {
      errorMessage = 'This reset link has expired. Please request a new one.';
    } else if (error.code === 'auth/invalid-action-code') {
      errorMessage = 'This reset link is invalid or has already been used.';
    } else if (error.code === 'auth/user-disabled') {
      errorMessage = 'This account has been disabled.';
    } else if (error.code === 'auth/user-not-found') {
      errorMessage = 'No account found for this reset link.';
    }

    return { valid: false, error: errorMessage };
  }
}

/**
 * Reset password using a valid reset token.
 *
 * @param token - The password reset token from the URL (oobCode)
 * @param newPassword - The new password to set
 * @returns Promise with reset result
 */
export async function resetPasswordWithToken(token: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
  try {
    // Confirm the password reset with Firebase
    await confirmPasswordReset(auth, token, newPassword);

    return { success: true };
  } catch (error: any) {
    console.error('resetPasswordWithToken error:', error);

    // Map Firebase error codes to user-friendly messages
    let errorMessage = 'Failed to reset password. Please try again.';
    if (error.code === 'auth/expired-action-code') {
      errorMessage = 'This reset link has expired. Please request a new one.';
    } else if (error.code === 'auth/invalid-action-code') {
      errorMessage = 'This reset link is invalid or has already been used.';
    } else if (error.code === 'auth/weak-password') {
      errorMessage = 'Password is too weak. Please use a stronger password.';
    } else if (error.code === 'auth/user-disabled') {
      errorMessage = 'This account has been disabled.';
    } else if (error.code === 'auth/user-not-found') {
      errorMessage = 'No account found for this reset link.';
    }

    return { success: false, error: errorMessage };
  }
}

